import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api';
import CowAvatar from './CowAvatar';

export default function NotificationBell() {
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setNotifs(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const count = notifs.length;

  const handleFriendRequest = async (notif, action) => {
    setLoading(true);
    try {
      if (action === 'accept') await api.post(`/friends/accept/${notif.id}`);
      else await api.delete(`/friends/${notif.id}`);
      await load();
    } finally {
      setLoading(false);
    }
  };

  const handleInvitation = async (notif, action) => {
    setLoading(true);
    try {
      if (action === 'accept') {
        const res = await api.post(`/vaquita-invitations/${notif.id}/accept`);
        await load();
        if (res.data?.inviteCode) navigate(`/vaquita/code/${res.data.inviteCode}`);
      } else {
        await api.post(`/vaquita-invitations/${notif.id}/reject`);
        await load();
      }
    } finally {
      setLoading(false);
    }
  };

  const typeLabel = { FRIEND_REQUEST: 'Amistad', VAQUITA_INVITATION: 'Vaquita', DEBT: 'Deuda', PAYMENT_CONFIRMATION: 'Pago' };
  const typeIcon  = { FRIEND_REQUEST: '🤝', VAQUITA_INVITATION: '🐮', DEBT: '💸', PAYMENT_CONFIRMATION: '💰' };

  return (
    <div className="bell-wrap" ref={dropdownRef}>
      <button className="bell-btn" onClick={() => setOpen(o => !o)} aria-label="Notificaciones">
        🔔
        {count > 0 && <span className="bell-badge">{count > 9 ? '9+' : count}</span>}
      </button>

      {open && (
        <div className="notif-dropdown">
          <div className="notif-header">
            <span>Notificaciones</span>
            {count > 0 && <span className="notif-count">{count}</span>}
          </div>

          {count === 0 && <div className="notif-empty">Todo al día 🎉</div>}

          {notifs.map((n, i) => (
            <div key={i} className={`notif-item notif-${n.type.toLowerCase()}`}>
              <div className="notif-row">
                <div className="notif-avatar">
                  {n.type === 'FRIEND_REQUEST' && <CowAvatar seed={n.profilePicture || n.userId} size={34} />}
                  {n.type === 'VAQUITA_INVITATION' && <CowAvatar seed={n.inviterPicture || n.inviterId} size={34} />}
                  {n.type === 'DEBT' && <CowAvatar seed={n.lenderPicture || n.lenderId} size={34} />}
                </div>
                <div className="notif-text">
                  {n.type === 'FRIEND_REQUEST' && (
                    <span><strong>{n.userName}</strong> te envió una solicitud de amistad</span>
                  )}
                  {n.type === 'VAQUITA_INVITATION' && (
                    <span><strong>{n.inviterName}</strong> te invitó a <strong>{n.vaquitaName}</strong></span>
                  )}
                  {n.type === 'DEBT' && (
                    <span>Le debes <strong>${n.amount?.toLocaleString('es-CL')}</strong> a <strong>{n.lenderName}</strong> en {n.vaquitaName}</span>
                  )}
                  {n.type === 'PAYMENT_CONFIRMATION' && (
                    <span><strong>{n.borrowerName}</strong> dice que te pagó <strong>${n.amount?.toLocaleString('es-CL')}</strong> — <button className="notif-btn view" onClick={() => { setOpen(false); navigate(`/notifications`); }}>Ver</button></span>
                  )}
                </div>
                <span className="notif-type-icon">{typeIcon[n.type]}</span>
              </div>

              {n.type === 'FRIEND_REQUEST' && (
                <div className="notif-actions">
                  <button className="notif-btn accept" disabled={loading} onClick={() => handleFriendRequest(n, 'accept')}>Aceptar</button>
                  <button className="notif-btn reject" disabled={loading} onClick={() => handleFriendRequest(n, 'reject')}>Rechazar</button>
                </div>
              )}
              {n.type === 'VAQUITA_INVITATION' && (
                <div className="notif-actions">
                  <button className="notif-btn accept" disabled={loading} onClick={() => handleInvitation(n, 'accept')}>Unirme</button>
                  <button className="notif-btn reject" disabled={loading} onClick={() => handleInvitation(n, 'reject')}>Rechazar</button>
                </div>
              )}
              {n.type === 'DEBT' && (
                <div className="notif-actions">
                  <button className="notif-btn view" onClick={() => { setOpen(false); navigate(`/vaquita/code/${n.inviteCode || ''}`); }}>
                    Ver vaquita
                  </button>
                </div>
              )}
            </div>
          ))}

          <Link to="/notifications" className="notif-see-all" onClick={() => setOpen(false)}>
            Ver todas las notificaciones →
          </Link>
        </div>
      )}

      <style jsx>{`
        .bell-wrap {
          position: relative;
        }

        .bell-btn {
          background: none;
          border: none;
          font-size: 1.2rem;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 50%;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s;
          line-height: 1;
        }

        .bell-btn:hover { background: var(--bg-primary); }

        .bell-badge {
          position: absolute;
          top: 2px;
          right: 2px;
          background: #ef4444;
          color: white;
          font-size: 0.6rem;
          font-weight: 700;
          border-radius: 999px;
          min-width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 3px;
          line-height: 1;
        }

        .notif-dropdown {
          position: fixed;
          top: 60px;
          right: 0.75rem;
          width: 320px;
          background: var(--bg-secondary);
          border-radius: 12px;
          box-shadow: 0 8px 32px var(--shadow-color);
          border: 1px solid var(--border-color);
          z-index: 2000;
          max-height: calc(100vh - 80px);
          overflow-y: auto;
        }

        @media (max-width: 768px) {
          .notif-dropdown {
            left: 0.75rem;
            right: 0.75rem;
            width: auto;
          }
        }

        .notif-header {
          padding: 0.75rem 1rem;
          font-weight: 700;
          font-size: 0.88rem;
          color: var(--text-primary);
          border-bottom: 1px solid var(--border-color);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .notif-count {
          background: #ef4444;
          color: white;
          font-size: 0.7rem;
          font-weight: 700;
          border-radius: 999px;
          padding: 1px 7px;
        }

        .notif-empty {
          padding: 2rem;
          text-align: center;
          color: var(--text-muted);
          font-size: 0.9rem;
        }

        .notif-item {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid var(--border-color);
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .notif-item:last-child { border-bottom: none; }

        .notif-row {
          display: flex;
          align-items: flex-start;
          gap: 0.6rem;
        }

        .notif-avatar { flex-shrink: 0; padding-top: 2px; }

        .notif-text {
          flex: 1;
          font-size: 0.85rem;
          color: var(--text-secondary);
          line-height: 1.4;
        }

        .notif-type-icon {
          font-size: 1rem;
          flex-shrink: 0;
        }

        .notif-actions {
          display: flex;
          gap: 0.4rem;
          padding-left: calc(34px + 0.6rem);
        }

        .notif-btn {
          padding: 0.3rem 0.75rem;
          border-radius: 8px;
          border: none;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          transition: opacity 0.15s;
        }

        .notif-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .notif-btn:hover:not(:disabled) { opacity: 0.85; }

        .notif-btn.accept { background: var(--accent-color); color: white; }
        .notif-btn.reject { background: transparent; color: var(--danger-color); border: 1px solid var(--danger-color); }
        .notif-btn.view   { background: var(--bg-primary); color: var(--text-secondary); border: 1px solid var(--border-color); }

        .notif-see-all {
          display: block;
          text-align: center;
          padding: 0.75rem;
          font-size: 0.83rem;
          font-weight: 600;
          color: var(--accent-color);
          text-decoration: none;
          border-top: 1px solid var(--border-color);
          transition: background 0.15s;
        }

        .notif-see-all:hover { background: var(--bg-primary); }
      `}</style>
    </div>
  );
}
